import React, { useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { AnyObject } from 'final-form';
import useFetch from 'use-http';

interface IContractorsBilledDialogProps {
    dialogName: string;
    handleReport?: (data: AnyObject) => void;
    hideDialog: () => void;
}

// marginRight is set explicitly here (rather than relying on RHFInputBox's own built-in 1rem) because
// RHFSelect's label doesn't add one on its own - without this the gap before the dropdown was
// narrower than the gap before the two period inputs.
const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem', marginRight: '1rem' };
const fieldStyle: React.CSSProperties = { width: '10rem' };

// Static help text, verbatim from the Oracle Forms yellow hint box (contractors_billed.fmt).
const DSCR_TEXT =
`Contractor Report Options:
ACTIVE -       All Active Contractors
INACTIVE -   All Inactive Contractors
BILLED -       All Contractors billed during a certain period of time
TERMED -    All Contractors termed during a certain period of time
USER -         All Personnel with an Active User Account
EVAL -          All Contractors hired/rehired in last 9 months
BENCH -      All Bench Contractors
ACTEMP -     All Active Employees
INAEMP -      All Inactive Employees`;

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Port of the Oracle Forms "Contractors Billed" screen (contractors_billed.fmt) - the "Contractors?"
// poplist itself picks which already-deployed SSRS report renders (ContractorsActive/ContractorsPeriod/
// ContractorsTermed/ContractorsEval/ActiveLogins/ContractorsBench/EmployeesActive - see ReportService.
// GetContractorsBilledReportFile), not a single shared proc's own parameter. Beginning/Ending Period
// only apply to BILLED, TERM, and EVAL. The source screen's Filename field (a client-side OLE Excel
// save-path picker) has no browser equivalent - dropped in favor of a fixed auto-download, same as
// every other converted report dialog in this repo.
const PERIOD_REPORT_TYPES = ['BILLED', 'TERM', 'EVAL'];

// The browser's <a download> attribute (used below) wins over any server-side Content-Disposition
// header, so the filename has to be picked here, not just in ReportsController.
// GetContractorsBilledReport - same reasoning as InvoiceOfficeBreakdownDialog.tsx's REPORT_FILE_NAMES.
// Every report type used to download as the same fixed "ContractorsBilled.xlsx", which made it easy
// to mistake a stale downloaded file for a fresh one when testing back-to-back report types.
const REPORT_FILE_NAMES: Record<string, string> = {
    ACTIVE: 'ContractorsActive.xlsx',
    INACTIVE: 'ContractorsInactive.xlsx',
    BILLED: 'ContractorsBilled.xlsx',
    TERM: 'ContractorsTermed.xlsx',
    EVAL: 'ContractorsEval.xlsx',
    USER: 'ActiveLogins.xlsx',
    BENCH: 'ContractorsBench.xlsx',
    ACTEMP: 'EmployeesActive.xlsx',
    INAEMP: 'EmployeesInactive.xlsx'
};

const ContractorsBilledDialog = (props: IContractorsBilledDialogProps) => {
    const { register, errors, getValues, watch } = useForm();
    const { post, response } = useFetch();
    const [loading, setLoading] = useState<boolean>(false);
    const reportType = watch('reportOptions.reportType', 'ACTIVE');
    const periodsApply = PERIOD_REPORT_TYPES.includes(reportType);

    const OKClicked = async () => {
        const data = getValues();
        const reportOptions = data?.reportOptions;

        setLoading(true);

        try {
            await post('/v1.0/reports/contractors-billed', {
                reportType: reportOptions?.reportType || 'ACTIVE',
                begPeriod: reportOptions?.begPeriod,
                endPeriod: reportOptions?.endPeriod
            });

            if (response.ok) {
                const blob = await response.blob();
                downloadBlobResponseAsFile(blob, REPORT_FILE_NAMES[reportOptions?.reportType] || 'ContractorsBilled.xlsx');
            }
        } catch (e) {
            console.log('Unable to generate Contractors report.', e);
        }

        setLoading(false);
        props.handleReport?.(reportOptions);
        props.hideDialog();
    };

    return (
        <Dialog title="Build Contractors Report" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '32%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '90%' }}>
                    <RHFSelect
                        label="Contractors?"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.reportType"
                        options={[
                            { value: 'ACTIVE', label: 'ACTIVE' },
                            { value: 'INACTIVE', label: 'INACTIVE' },
                            { value: 'BILLED', label: 'BILLED' },
                            { value: 'TERM', label: 'TERMED' },
                            { value: 'USER', label: 'USER' },
                            { value: 'EVAL', label: 'EVAL' },
                            { value: 'BENCH', label: 'BENCH' },
                            { value: 'ACTEMP', label: 'ACTEMP' },
                            { value: 'INAEMP', label: 'INAEMP' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="ACTIVE"
                        style={fieldStyle}
                    />
                    <RHFInputBox
                        label="Beginning Period"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.begPeriod"
                        register={register}
                        errors={errors}
                        disabled={!periodsApply}
                        style={fieldStyle}
                    />
                    <RHFInputBox
                        label="Ending Period"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.endPeriod"
                        register={register}
                        errors={errors}
                        disabled={!periodsApply}
                        style={fieldStyle}
                    />
                </form>
            </div>
            <div
                style={{
                    margin: '1rem 5% 0',
                    padding: '0.75rem',
                    backgroundColor: '#fff9c4',
                    border: '1px solid #d4c400',
                    whiteSpace: 'pre-line',
                    fontSize: '0.85rem'
                }}
            >
                {DSCR_TEXT}
            </div>
            <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={loading ? 'Generating' : 'OK'}
                    disabled={loading}
                    loading={loading}
                    onClick={() => !loading && OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'Cancel'}
                    disabled={loading}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default ContractorsBilledDialog;



// WEBPACK FOOTER //
// ./src/components/reports/dialogs/ContractorsBilledDialog.tsx